/* eslint-disable react-hooks/exhaustive-deps */
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import GoogleFontLoader from "react-google-font-loader";
import { AppBar, Avatar, Box, ButtonBase, CssBaseline, Menu as MuiMenu, MenuItem, Slide, Toolbar, Typography, useScrollTrigger, Alert, AlertTitle, IconButton, Stack, useTheme } from "@mui/material";
import { Editor } from "@craftjs/core";
import { db } from "@/firebase";
import { onSnapshot, doc } from "firebase/firestore";
import lz from "lzutf8";
import { Style } from "react-style-tag";
import Canvas, { Section } from "@/features/dine-in-flow/Canvas";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useAppDispatch } from "@/hooks";
import { deleteOrder, updateOrder } from "@/features/order/slice";
import RenderNode from "@/features/dine-in-flow/RenderNode";
import fonts from "@/constants/fonts";
import { logOut } from "@/features/auth/slice";
import MenuBottomNavigation from "@/components/MenuBottomNavigation";
import MenuSidebarNavigation from "@/components/MenuSidebarNavigation";
import { useResponsive } from "@/hooks";
import ContainerBlock from "@/components/editor/blocks/ContainerBlock";
import TextBlock from "@/components/editor/blocks/TextBlock";
import ImageBlock from "@/components/editor/blocks/ImageBlock";
import VerticalSpacerBlock from "@/components/editor/blocks/VerticalSpacerBlock";
import DishBlock from "@/components/editor/blocks/DishBlock";
import DropRegionBlock from "@/components/editor/blocks/DropRegionBlock";
import SectionBlock from "@/components/editor/blocks/section-block/SectionBlock";
import AppConfig from "@/components/AppConfig";
import { Configs } from "@/constants/configs";
import useAccessControl from "@/features/access-control/useAccessControl";
import { useFetchTemplateByIdQuery } from "@/api/templatesApi";
import { Order } from "@/types/orders";
import { Content, Menu, Template } from "@/types";
import { WebsiteContext } from "../website/WebsiteProvider";
import { useFetchMenuByIdQuery } from "@/features/organizations/api/menusApi";
import { useFetchContentQuery } from "@/features/organizations/api/contentApi";
import ProductDetails from "@/features/product-details/ProductDetails";
import RegionBlock from "@/components/editor/blocks/RegionBlock";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowLeft02Icon, ViewOffSlashIcon } from "@hugeicons/core-free-icons";
import useRoutes from "@/hooks/useRoutes";
import SelectedDineMode from "@/components/SelectedDineMode";
import "./Menu.scss";
import { IMAGE_PLACEHOLDER } from "@/constants/global";
import CatalogReader from "../catalog/components/CatalogReader";

interface HeaderProps {
  title: string;
}

const HideOnScroll = (props: any) => {
  const { children } = props;

  const trigger = useScrollTrigger({
    threshold: 100,
  });

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
};

interface IMenuContext {
  menu: Menu | null;
  template: Template | null;
  content: Content[];
  sections: Section[];
  json?: string;
}

export const MenuContext = createContext<IMenuContext>({
  menu: null,
  template: null,
  content: [],
  sections: [],
});

const Header = (props: HeaderProps) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [goToRoute] = useRoutes();
  const { isMobile } = useResponsive();

  const { organizationId, organizationAlias } = useParams<{
    organizationId: string;
    organizationAlias: string;
  }>();

  const [openUserMenu, setOpenUserMenu] = useState(false);

  const handleMenu = (event: any) => {
    setOpenUserMenu(event.currentTarget);
  };

  const handleClose = () => {
    setOpenUserMenu(false);
  };

  const handleLogOut = () => {
    dispatch(logOut());
    navigate(`/comercios/${organizationId}`);
  };

  const handleGoBack = () => {
    goToRoute(organizationAlias ? "organizations.menu.main" : "menus");
  };

  return (
    <div className="menu-viewer__header">
      <CssBaseline />
      <HideOnScroll>
        <AppBar
          elevation={2}
          color="default"
          sx={{
            width: isMobile ? "100%" : "calc(100% - 280px)",
            ml: isMobile ? 0 : "280px",
          }}
        >
          <Toolbar>
            <Stack direction="row" flex={1} gap={2} alignItems="center">
              <IconButton onClick={handleGoBack}>
                <HugeiconsIcon icon={ArrowLeft02Icon} color={theme.palette.primary.main} size={32} />
              </IconButton>
              <Typography variant="h4" component="div" sx={{ flexGrow: 1 }} color={"black"}>
                {props.title}
              </Typography>
            </Stack>
            <SelectedDineMode />
            <div>
              <AppConfig isEnabled={Configs.DRAWER_SIGNIN}>
                <ButtonBase onClick={handleMenu}>
                  <Avatar src={IMAGE_PLACEHOLDER} sx={{ height: "40px", width: "40px" }} />
                </ButtonBase>
                <MuiMenu
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  open={openUserMenu}
                  onClose={handleClose}
                >
                  <MenuItem onClick={handleLogOut}>Cerrar Sesión</MenuItem>
                </MuiMenu>
              </AppConfig>
            </div>
          </Toolbar>
        </AppBar>
      </HideOnScroll>
    </div>
  );
};

const MenuReader = () => {
  const { isMobile } = useResponsive();
  const dispatch = useAppDispatch();
  const [json, setJson] = useState();
  const [sections, setSections] = useState<Section[]>([]);
  const { search } = useLocation();
  const { menuId } = useParams<{ menuId: string }>();
  const { organization } = useContext(WebsiteContext);
  const { isActiveUserAdmin } = useAccessControl();

  const contentQuery = useFetchContentQuery(organization?.id ?? skipToken, {
    skip: !organization?.id,
  });

  const menuQuery = useFetchMenuByIdQuery(
    { menuId: menuId ?? skipToken, organizationId: organization?.id ?? skipToken },
    {
      skip: !menuId || !organization?.id,
    }
  );

  const templateQuery = useFetchTemplateByIdQuery(menuQuery?.data?.template || skipToken, {
    skip: !menuQuery?.data?.template,
  });

  useEffect(() => {
    const qs = new URLSearchParams(search);
    const tableId = qs.get("mesa");

    if (!organization?.id || !tableId) return;

    // Subscribe to orders
    dispatch({ type: "orders/subscribeToOrdersByTable" });
    const docRef = doc(db, `organizations/${organization?.id}/orders/${tableId}`);

    const unsubscribe = onSnapshot(
      docRef,
      (doc) => {
        if (doc.exists()) {
          const order = doc.data();

          dispatch(
            updateOrder({
              ...order,
              id: doc.id,
            } as Order)
          );
        } else {
          dispatch(deleteOrder({} as any));
        }
      },
      (error) => {
        console.error(error);
      }
    );

    return () => {
      unsubscribe();
      dispatch({ type: "orders/unsubscribeToOrdersByTable" });
    };
  }, [search]);

  const isCatalog = menuQuery?.data && (!menuQuery?.data?.flow || menuQuery?.data?.flow.length === 0);

  const sectionList = useMemo(() => {
    if (!isCatalog) {
      return sections;
    }
    const structure = menuQuery?.data?.structure || [];
    return structure
      .filter((s: any) => s.isVisible !== false)
      .map((s: any) => ({
        id: s.category,
        label: s.categoryTitle || "Sin título",
        slug: s.category,
      }));
  }, [menuQuery?.data?.structure, isCatalog, sections]);

  useEffect(() => {
    if (menuQuery?.data?.data || templateQuery?.data?.data) {
      const json = lz.decompress(lz.decodeBase64(menuQuery?.data?.data ?? templateQuery?.data?.data ?? ""));
      setJson(json);
    }
  }, [menuQuery?.data?.data, templateQuery.data?.data]);

  if (menuQuery.isLoading || contentQuery.isLoading || (!isCatalog && templateQuery.isLoading)) {
    return null;
  }

  if (!menuQuery?.data?.isPublished && !isActiveUserAdmin) {
    return null;
  }

  if (!isCatalog && !json) return null;

  return (
    <MenuContext.Provider
      value={{
        menu: menuQuery?.data ?? null,
        template: templateQuery?.data ?? null,
        content: contentQuery?.data ?? [],
        json,
        sections,
      }}
    >
      <Box sx={{ display: "flex", minHeight: "100vh" }}>
        {!isMobile && <MenuSidebarNavigation />}

        <Box
          component="main"
          className="menu-viewer"
          sx={{
            flexGrow: 1,
            width: isMobile ? "100%" : "calc(100% - 280px)",
            ml: isMobile ? 0 : "280px",
            overflow: isMobile ? "scroll" : "visible",
            overflowY: isMobile ? "scroll" : "visible",
            boxSizing: "border-box",
            paddingTop: isMobile && (menuQuery?.data?.isPublished || !isActiveUserAdmin) ? "55px" : 0,
            backgroundColor: 'white !important',
          }}
        >
          <GoogleFontLoader fonts={fonts} />

          {!menuQuery?.data?.isPublished && isActiveUserAdmin ? (
            <Box padding={2}>
              <Alert icon={<HugeiconsIcon icon={ViewOffSlashIcon} />} severity="warning">
                <AlertTitle>No publicado</AlertTitle>
                Menú no publicado. Solo tu puedes verlo por ser administrador.
              </Alert>
            </Box>
          ) : (
            isMobile && <Header title={menuQuery?.data?.title ?? ""} />
          )}

          {isCatalog ? (
            <CatalogReader menu={menuQuery.data} content={contentQuery.data || []} />
          ) : (
            <Editor
              resolver={{
                SectionBlock,
                ContainerBlock,
                TextBlock,
                ImageBlock,
                VerticalSpacerBlock,
                DishBlock,
                DropRegionBlock,
                RegionBlock,
              }}
              enabled={false}
              onRender={RenderNode}
            >
              <Style className={"viewport"} children={templateQuery.data?.styles ?? ""} />
              <Canvas json={json} setSections={setSections} />
            </Editor>
          )}
          <ProductDetails />
          {isMobile && (
            <div className="menu-viewer__navigation">
              <MenuBottomNavigation sections={sectionList as Section[]} />
            </div>
          )}
        </Box>
      </Box>
    </MenuContext.Provider>
  );
};

export default MenuReader;
